import React, { useContext } from 'react';
import { CoursePricingOption } from '../../../../../../../store/features/coursePricing';
import { CourseOptionDisplayModuleContext } from '../../../context';
import { getDeliveryTypeName } from '../../../../../../../helpers/getDeliveryTypeName';
import { getOptionDurationValue, getOptionTimeValue } from '../../../helpers';
import {
  DiscountParagraph,
  Link,
  Paragraph,
  PriceParagraph,
  SalesMessage,
  StyledBookButton,
  StyledDeliveryType,
  StyledDownloadBrochureLink,
  FlexEnd,
  TierParagraph,
  BoldParagraph,
} from '../../../styledComponents';
import {
  BookButton,
  DownloadBrochure,
  Language,
  Location,
  StartDate,
  Tier,
  Trainer,
} from '../../../dataComponents';

export type TableRowProps = {
  row: CoursePricingOption;
};

export function TableRow({ row }: TableRowProps) {
  const {
    section: {
      showSettings: {
        showDate,
        showTime,
        showDuration,
        showLocation,
        showTimezone,
        showLanguage,
        showDownloadBrochureLink,
        showVenue,
        showSalesMessage,
        showTrainers,
      },
    },
  } = useContext(CourseOptionDisplayModuleContext);

  const optionDuration = getOptionDurationValue(
    showDuration,
    row.amountOfDays,
    row.numberOfDaysOverride,
  );

  const optionTime = getOptionTimeValue({
    showTime,
    showTimezone,
    time: row.time,
    timeZone: row.timeZone,
    timeZoneAsText: row.timeZoneAsText,
  });

  return (
    <tr>
      {showDate && (
        <td>
          {(row.startDate || row.dateAsText) && (
            <>
              {row.startDate ? (
                <StartDate value={row.startDate} as={BoldParagraph} />
              ) : (
                <BoldParagraph text={row.dateAsText} />
              )}
              {optionDuration && <Paragraph text={optionDuration} />}
              {optionTime && <Paragraph text={optionTime} />}
            </>
          )}
        </td>
      )}
      <td>
        <StyledDeliveryType text={getDeliveryTypeName(row.deliveryType)} />
        {row.location && (showLocation || showVenue) && (
          <Location
            showLocation={showLocation}
            showVenue={showVenue}
            location={row.location}
            as={Paragraph}
          />
        )}
      </td>
      {showTrainers && (
        <td>
          {row.trainers.map((trainer, trainerIndex) => {
            return (
              <Trainer
                key={trainerIndex}
                uri={trainer.uri}
                displayName={trainer.displayName}
                as={Link}
              />
            );
          })}
        </td>
      )}
      {(showLanguage || showDownloadBrochureLink) && (
        <td>
          {showLanguage && row.language && (
            <Language value={row.language} as={Paragraph} />
          )}
          {showDownloadBrochureLink && (
            <DownloadBrochure
              ctaUrl={row.brochureCtaUrl}
              ctaText={row.brochureCtaText}
              newTab={row.openBrochureInNewTab}
              as={StyledDownloadBrochureLink}
            />
          )}
        </td>
      )}
      <td style={{ textAlign: 'right' }}>
        <FlexEnd>
          {row.primaryProductDiscountedPrice ? (
            <>
              <DiscountParagraph text={row.primaryProductFullPrice} />
              <PriceParagraph text={row.primaryProductDiscountedPrice} />
            </>
          ) : (
            <PriceParagraph text={row.primaryProductFullPrice} />
          )}
        </FlexEnd>
        {row.tier?.endDate && row.tier?.maxSaving && (
          <Tier
            endDate={row.tier.endDate}
            maxSaving={row.tier.maxSaving}
            as={TierParagraph}
          />
        )}
      </td>
      <td>
        <BookButton
          bookingOpen={row.bookingOpen}
          bookingClosedText={row.bookingClosedText}
          bookingLinkOverride={row.bookingLinkOverride}
          bookingButtonTextOverride={row.bookingButtonTextOverride}
          optionIndex={row.index}
          as={StyledBookButton}
        />
        {showSalesMessage && row.salesMessage && (
          <SalesMessage text={row.salesMessage} />
        )}
      </td>
    </tr>
  );
}
